import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import StorageMartHeaderSearch from '../../../../../src/web/containers/StorageMartHeaderSearch';
import { TRACK_CLICK_NAV_SEARCH_BY_ZIP } from '../../../../../src/web/utils/dataLayer';
import StorageMartNav from '../../../../../src/web/containers/StorageMartNav';
import Icon from '../../../../../modules/react/components/stateless/Icon';
import Translation from '../../../../components/stateless/Translation';
import StorageMartLanguageMenu from '../../../../../src/web/containers/StorageMartLanguageMenu';
import styles from './styles.scss';
import storageMartTabLogo from './storagemart-tab-logo.svg';
import mmsHeaderLogo from './mms-header-logo.svg';
import mmsHeaderLogoBlack from './mms-header-logo-black.svg';
import mmsTabLogo from './mms-tab-logo.svg';
import mmsTabLogoMobile from './mms-tab-logo-mobile.svg';
import useMedia from '../../../../../modules/react/hooks/useMedia';
import { MD_MIN_PX } from '../../../../../modules/theme/breakpoints';
import intlPath from '../../../../../src/web/utils/intlPath';

const StorageMartCombinedHeader = ({
  hideLanguageMenu,
  storageMartTabPath = '',
  mmsTabPath = '',
  hideNavigation,
  logoType,
}) => {
  const intl = useIntl();
  const [nav, setNav] = useState(false);
  const [search, setSearch] = useState(false);

  const onShowSearch = () => setSearch(true);
  const onHideSearch = () => setSearch(false);
  const onToggleNav = () => setNav(!nav);
  const closeMobileNav = () => setNav(false);
  const menuButtonText = nav ? 'close menu' : 'open menu';
  const menuButtonIcon = nav ? 'exit' : 'bars';
  const storageMartLinkAttrs = storageMartTabPath.startsWith('http')
    ? {
      href: storageMartTabPath,
      target: '_blank',
      rel: 'noreferrer',
    }
    : {
      href: intlPath(intl.locale, storageMartTabPath),
    };
  const mmsLinkAttrs = mmsTabPath.startsWith('http')
    ? {
      href: mmsTabPath,
      target: '_blank',
      rel: 'noreferrer',
    }
    : {
      href: mmsTabPath,
    };

  return (
    <div className={`${styles.header} ${nav ? styles.navVisible : ''}  ${search ? styles.searchVisible : ''}`}>
      {!hideNavigation && (
        <div className="flex gap-2 justify-between w-full bg-light-gray">
          <div className="flex gap-2 justify-between w-full md:max-w-[330px] lg:max-w-390 min-h-50">
            <a
              {...storageMartLinkAttrs}
              className={`flex justify-center items-center w-2/4 py-2 px-8 md:px-4 ${logoType === 'storagemart' ? 'bg-white' : ''}`}
            >
              <img
                src={storageMartTabLogo}
                alt="StorageMart"
                width="80"
                height="12"
                loading="eager"
                className="relative top-0.5"
              />
            </a>
            <a
              {...mmsLinkAttrs}
              className={`flex justify-center items-center w-2/4 py-2 px-8 md:px-4 ${logoType === 'mms' ? 'bg-white' : ''}`}
            >
              <picture>
                <source media="(min-width: 860px)" srcSet={mmsTabLogo} />
                <source media="(max-width: 860px)" srcSet={mmsTabLogoMobile} />
                <img alt="Manhattan Mini Storage" src={mmsTabLogoMobile} className="w-full max-w-[80px] min-[860px]:max-w-190" width="80" height="29" loading="eager" />
              </picture>
            </a>
          </div>
          {useMedia(
            [`(min-width: ${MD_MIN_PX})`],
            [
              <div className="flex pr-4 items-center md:gap-8">
                <Translation
                  id="sections.enterprise.storageMartHeader.phoneNumber"
                  defaultMessage="1234567890"
                >
                  {phoneNumber => (
                    <a href={`tel:${phoneNumber}`} className="flex items-center gap-1 text-sm-green" data-marchex>
                      <Icon id="phone" label="Phone" className="h-8e w-4" />
                      {phoneNumber}
                    </a>
                  )}
                </Translation>
                {!hideLanguageMenu && (
                  <StorageMartLanguageMenu />
                )}
                <Translation
                  id="sections.enterprise.storageMartCombinedHeader.tenantPortal"
                  defaultMessage="Sign In"
                >
                  {text => (
                    <a
                      href={`https://my-account.storage-mart.com/?lang=${intl.locale}`}
                      className="p-header-a-sm lg:p-header-a-lg relative text-black border-1 border-t-none rounded-b bg-sm-btn-prim border-solid border-header-a-border -top-1 tracking-p43 hovcusact:bg-sm-btn-prim-hov hovcus:no-underline"
                    >
                      <span>{text}</span>
                    </a>
                  )}
                </Translation>
              </div>,
            ],
          )}
        </div>
      )
      }
      <div className={styles.preheader}>
        <div className="flex gap-2 items-center max-h-14 sm:flex-1 sm:w-1/5 md:gap-4 lg:w-1/4">
          {(logoType === 'storagemart' || logoType === 'combined') && (
            <a
              {...storageMartLinkAttrs}
              className={`w-full max-w-160 min-[1200px]:max-w-190 ${logoType === 'combined' ? 'pr-2 border-r-2 border-black md:pr-4' : ''}`}
            >
              <img
                alt="StorageMart Self Storage"
                loading="eager"
                src="https://uploads.storage-mart.com/a0756a77-5ab9-4c50-b715-fd5a68030d77/smlogo.svg"
                width="768"
                height="192"
                className="w-full"
              />
            </a>
          )}
          {(logoType === 'mms' || logoType === 'combined') && (
            <a {...mmsLinkAttrs} className="block">
              <img
                alt="Manhattan Mini Storage"
                src={logoType === 'combined' ? mmsHeaderLogoBlack : mmsHeaderLogo}
                width="184"
                height="66"
                loading="eager"
                className="max-h-12 w-full"
              />
            </a>
          )}
        </div>
        {!hideNavigation && (
          <>
            <div className={`flex items-center ${styles.controls}`}>
              <div className={styles.nav}>
                <StorageMartNav
                  hideLanguageMenu={hideLanguageMenu}
                  closeMobileNav={closeMobileNav}
                  navbarClassName={styles.navbar}
                  navItemClassName={styles.navItem}
                  id="storageMartCombinedHeader-nav"
                />
              </div>
              <Translation
                id="sections.enterprise.storageMartHeader.phoneNumber"
                defaultMessage="1234567890"
              >
                {phoneNumber => (
                  <a
                    href={`tel:${phoneNumber}`}
                    className="md:hidden p-2 w-40 border text-black rounded bg-sm-yellow border-solid border-black hovcus:no-underline"
                    title={phoneNumber}
                  >
                    <Icon id="phone" label="Phone" className="w-4 h-4 block" />
                  </a>
                )}
              </Translation>
              <Translation
                id="sections.enterprise.storageMartCombinedHeader.tenantPortal"
                defaultMessage="Sign In"
              >
                {text => (
                  <a
                    className="md:hidden p-2 w-40 border text-black rounded bg-sm-yellow border-solid border-black hovcus:no-underline"
                    href={`https://my-account.storage-mart.com/?lang=${intl.locale}`}
                    title={text}
                  >
                    <Icon id="user" label="Tenant Portal" className="w-4 h-4 block" />
                  </a>
                )}
              </Translation>
              <Translation
                id="sections.enterprise.storageMartHeader.a11yShowSearch"
                defaultMessage="Show search"
              >
                {text => (
                  <button
                    type="button"
                    onClick={onShowSearch}
                    className={`${styles.mobileHidden} md:flex items-center px-4 py-2.5 leading-tight`}
                    {...TRACK_CLICK_NAV_SEARCH_BY_ZIP}
                  >
                    <span className="sr-only">{text}</span>
                    <Icon id="fa-search" className="text-black" label="Search" />
                  </button>
                )}
              </Translation>
              <button type="button" onClick={onToggleNav} className={styles.showNav} aria-expanded={nav}>
                <Icon id={menuButtonIcon} className={styles.navIcon} label={menuButtonText} />
              </button>
            </div>
            {search && (
              <div className="flex items-center w-full md:max-w-[450px]">
                <StorageMartHeaderSearch onDismiss={onHideSearch} id="storageMartHeader-search" />
              </div>
            )}
          </>
        )}
      </div>
      {
        useMedia(
          [`(max-width: ${MD_MIN_PX})`],
          [
            !hideNavigation && (
              <div className={styles.nav}>
                <StorageMartNav
                  hideLanguageMenu={hideLanguageMenu}
                  closeMobileNav={closeMobileNav}
                  id="storageMartCombinedHeader-nav-mobile"
                />
              </div>
            ),
          ],
        )
      }
    </div>
  );
};


StorageMartCombinedHeader.propTypes = {
  children: PropTypes.node.isRequired,
  hideLanguageMenu: PropTypes.bool,
};

StorageMartCombinedHeader.defaultProps = {
  hideLanguageMenu: undefined,
};

export default StorageMartCombinedHeader;
